/**
 * The secure-link calls (H3.1). The API client loads on first use, so the
 * landing page paints before it. None of these sends an account's session:
 * the API never resolves an account under `/api/v1/links/`, and the link
 * session's own cookie is sent only there.
 */
import type {
  LinkCodeSent,
  LinkReportReason,
  LinkSessionInfo,
  LinkStep,
} from '@gradex-hire/contracts';

const client = () => import('../../lib/api/client.js').then((module) => module.api);

/** Opens a link: the next step (a code, or a link session). */
export async function openLink(linkId: string, secret: string): Promise<LinkStep> {
  return (await client()).post('/api/v1/links/open', { body: { linkId, secret } });
}

/** Sends (or resends) the code to the address the link is bound to. */
export async function sendLinkCode(linkId: string, secret: string): Promise<LinkCodeSent> {
  return (await client()).post('/api/v1/links/verify/send', { body: { linkId, secret } });
}

/** Enters the code; on success a link session starts. */
export async function verifyLink(linkId: string, secret: string, code: string): Promise<LinkStep> {
  return (await client()).post('/api/v1/links/verify', { body: { linkId, secret, code } });
}

/** The current link session. */
export async function linkSession(): Promise<LinkSessionInfo> {
  return (await client()).get('/api/v1/links/session');
}

/** Ends the link session ("Done"). */
export async function endLinkSession(): Promise<void> {
  await (await client()).post('/api/v1/links/session/end');
}

/** "Report this link": needs only the ID; always answers the same. */
export async function reportLink(
  linkId: string,
  reason: LinkReportReason,
  note: string,
): Promise<void> {
  await (await client()).post('/api/v1/links/report', { body: { linkId, reason, note } });
}
